/**
 * Variant Picker 共通ロジック（表示形式非依存）。
 * ラジオボタン形式（_tgtp-block-product-variant-picker）とプルダウン形式
 * （_tgtp-block-product-variant-picker-dropdown）の両ブロックの inline script から
 * 同期読み込み（defer なし）で利用する。二重読み込みは先勝ちで無視する。
 *
 * 責務:
 *   - 選択値の配列からの variant 一致判定・在庫切れ判定（純関数）
 *   - variant 確定時の共通副作用（cart form の hidden input / URL の variant パラメータ /
 *     buy-button への通知イベント / Section Rendering 更新中の操作ロック）。
 *     tgtp:variant-changed を購読する他ブロック（画像サムネイル・在庫僅少表示・
 *     再入荷通知ボタンなど）への連動もここから発火する
 *   - 商品単位の購入制限（buy-button の data-button-state）による picker 操作不可制御と
 *     Section Rendering 再描画後の再評価
 *
 * 表示形式固有の DOM 読み書き（選択値の読み取り・在庫切れの見せ方・change の配線）は
 * 各ブロックの inline script が持つ。
 */
(() => {
  if (window.tgtpVariantSelection) return;

  // 選択値の配列（オプション位置 0-index。product.options_with_values の順 =
  // variant.options の順）に完全一致する variant を返す。無ければ null。
  const findMatchingVariant = (variantData, selectedValues) =>
    variantData.find((variant) =>
      variant.options.every((optionValue, index) => optionValue === selectedValues[index]),
    ) || null;

  // 他オプションの現在の選択値を固定したまま対象の値を選んだ場合に、一致する variant が
  // （存在しない場合を含め）全て在庫切れ（available: false）なら true。
  // 在庫切れでも inventory_policy が continue（販売継続）の variant は
  // variant.available が true のまま返るため対象外になる。
  const isValueUnavailable = (variantData, selectedValues, optionIndex, value) => {
    const testValues = selectedValues.slice();
    testValues[optionIndex] = value;
    const matchingVariants = variantData.filter((variant) =>
      variant.options.every((optionValue, index) => optionValue === testValues[index]),
    );
    return matchingVariants.every((variant) => !variant.available);
  };

  // variant 確定時の共通副作用。
  const applyVariantSelection = (section, matchedVariant) => {
    // cart form の hidden input を更新
    const form = section.querySelector('form[action*="/cart/add"]');
    if (form) {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput) variantInput.value = matchedVariant.id;
    }

    // URL の variant パラメータを更新
    const url = new URL(window.location);
    url.searchParams.set('variant', matchedVariant.id);
    window.history.replaceState({}, '', url.toString());

    // buy-button 側の quantity-validation が新 variant で即時再計算できるよう先行更新する。
    // 直後の Section Rendering がサーバ側の正とする状態で上書きする。
    const buyButtonWrapper = section.querySelector('.tgtp-product-buy-button');
    if (buyButtonWrapper) {
      buyButtonWrapper.dataset.currentVariantId = matchedVariant.id;
      buyButtonWrapper.dispatchEvent(new CustomEvent('tgtp:variant-change', {
        bubbles: true,
        detail: { variantId: matchedVariant.id },
      }));
    }

    // Section Rendering 完了まで picker / quantity を操作不可にする。
    // is-refreshing は更新中インジケーター（スピナー等）の CSS フック。
    // 解除は buy-button 側の再描画処理（tgtp:section-refreshed 発火元）が
    // pointer-events と一緒に行う。buy-button block がセクションに無い
    // （= 再描画も解除も起きない）構成でも操作不能のまま残らないよう、
    // 安全タイムアウトで必ず解除する（解除済みなら no-op）。
    const picker = section.querySelector('.tgtp-product-variant-picker');
    if (picker) {
      picker.style.pointerEvents = 'none';
      picker.classList.add('is-refreshing');
    }
    const quantity = section.querySelector('.tgtp-product-quantity');
    if (quantity) quantity.style.pointerEvents = 'none';
    setTimeout(() => {
      if (picker) {
        picker.style.pointerEvents = '';
        picker.classList.remove('is-refreshing');
      }
      if (quantity) quantity.style.pointerEvents = '';
    }, 5000);

    // buy-button の Section Rendering 再取得と、サムネイル等の購読ブロックへの通知
    section.dispatchEvent(new CustomEvent('tgtp:variant-changed', {
      detail: { variantId: matchedVariant.id },
    }));
  };

  // 商品単位の購入制限が有効なときは picker を操作不可にする。
  // buy-button が算出した状態（data-button-state）を唯一のソースとして読む。
  // 在庫切れ(sold_out)・購入数制限(exceed_quantity)は variant を切り替えると解消しうるので対象外。
  // 購入済み(cant_purchase_twice)は _limit-variant: で variant 単位になりうるため対象外。
  // ログイン必須(login_required)はログインで解消し variant 選択自体は妨げないため対象外。
  const PRODUCT_LEVEL_RESTRICTED = ['before_sale', 'after_sale', 'stop_sale', 'bad_combination'];

  // 初回（buy-button が picker より後ろに置かれるケースに備え DOM 確定後）と
  // Section Rendering 再描画後に評価し、picker の is-restricted / aria-disabled を
  // 切り替えたうえで applyState(restricted) を呼ぶ。表示形式固有の後処理
  // （input / select の disabled 同期・在庫切れ表示の再評価）は applyState 側で行う。
  const setupRestrictionState = (section, picker, applyState) => {
    const evaluate = () => {
      const buyButton = section.querySelector('.tgtp-product-buy-button');
      const state = buyButton ? buyButton.dataset.buttonState : '';
      const restricted = PRODUCT_LEVEL_RESTRICTED.indexOf(state) !== -1;
      picker.classList.toggle('is-restricted', restricted);
      picker.setAttribute('aria-disabled', restricted ? 'true' : 'false');
      applyState(restricted);
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', evaluate);
    } else {
      evaluate();
    }
    section.addEventListener('tgtp:section-refreshed', evaluate);
  };

  window.tgtpVariantSelection = {
    findMatchingVariant,
    isValueUnavailable,
    applyVariantSelection,
    setupRestrictionState,
  };
})();
